<script setup>
/**
 * This component handles parsing and sending WhatsApp message templates.
 * It works as follows:
 * 1. Displays the template text with variable placeholders.
 * 2. Generates input fields for each variable in the template.
 * 3. Validates that all variables are filled before sending.
 * 4. Replaces placeholders with user-provided values.
 * 5. Emits events to send the processed message or reset the template.
 */
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import { DirectUpload } from 'activestorage';
import { useAlert } from 'dashboard/composables';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';

import FileUpload from 'vue-upload-component';
import WhatsappMessagePreview from 'dashboard/routes/dashboard/settings/campaigns/WhatsappMessagePreview.vue';
import { isWhatsAppComplete } from '@chatwoot/utils';
import Input from 'dashboard/components-next/input/Input.vue';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';
import {
  buildTemplateParameters,
  allKeysRequired,
  DEFAULT_LANGUAGE,
  DEFAULT_CATEGORY,
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
  renderTemplatePreview,
} from 'dashboard/helper/templateHelper';

const props = defineProps({
  template: {
    type: Object,
    default: () => ({}),
    validator: value => {
      if (!value || typeof value !== 'object') return false;
      if (!value.components || !Array.isArray(value.components)) return false;
      return true;
    },
  },
  sendRenderedContent: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['sendMessage', 'resetTemplate', 'back']);

const { t } = useI18n();
const store = useStore();

const processedParams = ref({});
const attachedFiles = ref([]);

const languageLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.LANGUAGE')}: ${props.template.language || DEFAULT_LANGUAGE}`;
});

const categoryLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.CATEGORY')}: ${props.template.category || DEFAULT_CATEGORY}`;
});

const headerComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.HEADER);
});

const bodyComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.BODY);
});

const bodyText = computed(() => {
  return bodyComponent.value?.text || '';
});

const headerText = computed(() => {
  return headerComponent.value?.format === 'TEXT'
    ? headerComponent.value?.text || ''
    : '';
});

const hasMediaHeader = computed(() =>
  MEDIA_FORMATS.includes(headerComponent.value?.format)
);

const formatType = computed(() => {
  const format = headerComponent.value?.format;
  return format ? format.charAt(0) + format.slice(1).toLowerCase() : '';
});

const isDocumentTemplate = computed(() => {
  return headerComponent.value?.format?.toLowerCase() === 'document';
});

const globalConfig = computed(() => store.getters['globalConfig/get']);
const currentChat = computed(() => store.getters.getSelectedChat);

const allowedFileTypes = computed(() => {
  const format = headerComponent.value?.format;
  if (format === 'VIDEO') return 'video/mp4';
  if (format === 'DOCUMENT') return 'application/pdf';
  if (format === 'IMAGE') return 'image/png, image/jpeg';
  return '';
});

const currentFileExtension = computed(() => {
  const format = headerComponent.value?.format;
  if (format === 'VIDEO') return '.mp4';
  if (format === 'DOCUMENT') return '.pdf';
  if (format === 'IMAGE') return '.png/.jpeg';
  return '';
});

const hasBodyVariables = computed(() => {
  return bodyText.value?.match(/{{([^}]+)}}/g) !== null;
});

const hasTextHeaderVariables = computed(() => {
  return headerText.value?.match(/{{([^}]+)}}/g) !== null;
});

const hasVariables = computed(
  () => hasBodyVariables.value || hasTextHeaderVariables.value
);

const renderedHeader = computed(() => {
  return renderTemplatePreview(
    headerText.value,
    processedParams.value.header || {}
  );
});

const renderedTemplate = computed(() => {
  return renderTemplatePreview(
    bodyText.value,
    processedParams.value.body || {}
  );
});

// Completeness validation is shared with the mobile app via @chatwoot/utils.
//
// MA: no cabeçalho de mídia a imagem é o anexo, não uma URL. O helper do
// upstream exige processedParams.header.media_url, que este fluxo nunca
// preenche — o backend resolve a mídia pelo download_url do anexo, e o
// build_media_header_params ignora justamente o media_url vazio. Sem o desvio
// abaixo, todo template com cabeçalho de mídia fica com o envio bloqueado.
const isFormInvalid = computed(() => {
  if (!hasMediaHeader.value) {
    return !isWhatsAppComplete(props.template, processedParams.value);
  }

  if (!attachedFiles.value.length) return true;

  // Só para a checagem: o que é enviado continua sendo processedParams.
  const paramsWithAttachedMedia = {
    ...processedParams.value,
    header: { ...processedParams.value.header, media_url: 'attachment' },
  };
  return !isWhatsAppComplete(props.template, paramsWithAttachedMedia);
});

const v$ = useVuelidate(
  {
    processedParams: {
      requiredIfKeysPresent: requiredIf(hasVariables),
      allKeysRequired,
    },
  },
  { processedParams }
);

const initializeTemplateParameters = () => {
  processedParams.value = buildTemplateParameters(
    props.template,
    hasMediaHeader.value
  );
};

const attachFile = ({ blob, file }) => {
  attachedFiles.value = [];
  const reader = new FileReader();
  reader.readAsDataURL(file.file);
  reader.onloadend = () => {
    attachedFiles.value.push({
      currentChatId: currentChat.value?.id,
      resource: blob || file,
      isPrivate: false,
      thumb: reader.result,
      blobSignedId: blob ? blob.signed_id : undefined,
    });
  };
};

const removeAttachment = itemIndex => {
  attachedFiles.value = attachedFiles.value.filter(
    (_, index) => itemIndex !== index
  );
};

const onFileUpload = file => {
  if (!file) return;
  if (globalConfig.value.directUploadsEnabled) {
    const maxSize = 40; // MB
    if (!checkFileSizeLimit(file, maxSize)) {
      useAlert(
        t('CONVERSATION.FILE_SIZE_LIMIT', {
          MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE: maxSize,
        })
      );
      return;
    }
    const upload = new DirectUpload(
      file.file,
      `/api/v1/accounts/${store.getters.getCurrentAccountId}/conversations/${currentChat.value?.id}/direct_uploads`,
      {
        directUploadWillCreateBlobWithXHR: xhr => {
          xhr.setRequestHeader(
            'api_access_token',
            store.getters.getCurrentUser?.access_token
          );
        },
      }
    );
    upload.create((error, blob) => {
      if (error) {
        useAlert(error);
      } else {
        attachFile({ file, blob });
      }
    });
  } else {
    attachFile({ file });
  }
};

const sendMessage = () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  const { name, category, language, namespace } = props.template;

  const payload = {
    message: props.sendRenderedContent
      ? renderedTemplate.value
      : bodyText.value,
    pendingMessageContent: renderedTemplate.value,
    private: false,
    templateParams: {
      name,
      category,
      language,
      namespace,
      content_mode: props.sendRenderedContent ? 'rendered' : 'raw_template',
      processed_params: processedParams.value,
    },
  };

  if (headerComponent.value) {
    payload.templateParams.header_params = {
      format: headerComponent.value.format,
    };
  }

  if (attachedFiles.value.length) {
    payload.files = [];
    if (globalConfig.value.directUploadsEnabled) {
      payload.files.push(attachedFiles.value[0].blobSignedId);
    } else {
      payload.files.push(attachedFiles.value[0].resource.file);
    }
  }

  emit('sendMessage', payload);
};

const resetTemplate = () => {
  emit('resetTemplate');
};

const goBack = () => {
  emit('back');
};

onMounted(initializeTemplateParameters);

watch(
  () => props.template,
  () => {
    initializeTemplateParameters();
    v$.value.$reset();
  },
  { deep: true }
);

defineExpose({
  processedParams,
  attachedFiles,
  hasVariables,
  hasMediaHeader,
  isDocumentTemplate,
  headerComponent,
  renderedHeader,
  renderedTemplate,
  isFormInvalid,
  v$,
  sendMessage,
  resetTemplate,
  goBack,
});
</script>

<template>
  <div>
    <div class="flex flex-col gap-4 p-4 mb-4 rounded-lg bg-n-alpha-black2">
      <div class="flex justify-between items-center">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ template.name }}
        </h3>
        <span class="text-xs text-n-slate-11">
          {{ languageLabel }}
        </span>
      </div>

      <div class="flex flex-col gap-2">
        <div class="-mx-4 -my-2">
          <WhatsappMessagePreview
            :message="renderedTemplate"
            :template="template"
          />
        </div>
      </div>

      <div class="text-xs text-n-slate-11">
        {{ categoryLabel }}
      </div>
    </div>

    <div v-if="hasVariables || hasMediaHeader">
      <div v-if="hasMediaHeader" class="mb-4">
        <p class="mb-2.5 text-sm font-semibold">
          {{
            $t('WHATSAPP_TEMPLATES.PARSER.MEDIA_HEADER_LABEL', {
              type: formatType,
            }) || `${formatType} Header`
          }}
        </p>
        <FileUpload
          ref="upload"
          input-id="templateAttachment"
          :size="4096 * 4096"
          :accept="allowedFileTypes"
          :multiple="false"
          drop
          :drop-directory="false"
          :data="{
            direct_upload_url: '/rails/active_storage/direct_uploads',
            direct_upload: true,
          }"
          @input-file="onFileUpload"
        >
          <woot-button
            :title="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
            icon="attach"
            emoji="📎"
            color-scheme="secondary"
            variant="smooth"
            :style="
              !attachedFiles.length ? 'border: 1px solid red' : 'border: none'
            "
          >
            {{
              $t('WHATSAPP_TEMPLATES.PARSER.ATTACH') +
              ` ${currentFileExtension}`
            }}
          </woot-button>
          <span
            v-if="!attachedFiles.length"
            class="pl-2 text-red-400 text-xs inline-block"
          >
            {{
              $t('WHATSAPP_TEMPLATES.PARSER.SUBMIT_FILE') +
              ` ${currentFileExtension}`
            }}
          </span>
        </FileUpload>
        <div v-if="attachedFiles.length" class="mt-2">
          <AttachmentPreview
            class="flex-col"
            :attachments="attachedFiles"
            :remove-attachment="removeAttachment"
          />
        </div>
      </div>

      <!-- Text Header Variables Section -->
      <div v-if="hasTextHeaderVariables && processedParams.header">
        <p class="mb-2.5 text-sm font-semibold">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.HEADER_VARIABLES_LABEL') }}
        </p>
        <div
          v-for="(variable, key) in processedParams.header"
          :key="`header-${key}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.header[key]"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER', {
                variable: key,
              })
            "
          />
        </div>
      </div>

      <!-- Body Variables Section -->
      <div v-if="processedParams.body">
        <p class="mb-2.5 text-sm font-semibold">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_LABEL') }}
        </p>
        <div
          v-for="(variable, key) in processedParams.body"
          :key="`body-${key}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.body[key]"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER', {
                variable: key,
              })
            "
          />
        </div>
      </div>

      <!-- Button Variables Section -->
      <div v-if="processedParams.buttons">
        <p class="mb-2.5 text-sm font-semibold">
          {{ t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETERS') }}
        </p>
        <div
          v-for="(button, index) in processedParams.buttons"
          :key="`button-${index}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.buttons[index].parameter"
            type="text"
            class="flex-1"
            :placeholder="t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETER')"
          />
        </div>
      </div>
      <p
        v-if="v$.$dirty && v$.$invalid"
        class="p-2.5 text-center rounded-md bg-n-ruby-9/20 text-n-ruby-9"
      >
        {{ $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}
      </p>
    </div>

    <slot
      name="actions"
      :send-message="sendMessage"
      :reset-template="resetTemplate"
      :go-back="goBack"
      :is-valid="!v$.$invalid"
      :disabled="isFormInvalid"
    />
  </div>
</template>
